// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AppConstants, LMDialogButtonKey } from '@utils/app.constants';
import { SohoAlertDirective, SohoSpinboxComponent } from 'ids-enterprise-ng';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { LMGlobalPersonalizationService } from '@app/states';
import { LMConfigurationService, LMModalWrapperService } from '@app/services';
import { LMFilterBuilderService } from '@app/components';
import { Observable, of } from 'rxjs';

@Component({
  selector:    'lm-filter-builder',
  standalone: false,
  templateUrl: './lm-filter-builder.component.html',
  styleUrls:   [ './lm-filter-builder.component.scss' ]
})
export class LMFilterBuilderComponent implements OnInit {

  @ViewChild(SohoSpinboxComponent) spinboxComponent: SohoSpinboxComponent;
  @ViewChild(SohoAlertDirective) alert: SohoAlertDirective;

  @Input() options: LMFilterBuilderOptions;

  @Output() filterData = new EventEmitter<LMFieldFilterData>();

  public static MATCH_OPTION_ALL = 'all';
  public static MATCH_OPTION_ANY = 'any';
  useCompactFormLayout = false;
  separator: string;

  buildAnotherLabel: string;
  uniqueRowIdentifier = 0;

  public model = {
    conditionString:   '',
    includeBaseFilter: false,
    matchOption:       '',
    businessClass:     '',
    listName:          '',
  };

  private _filterRows = new Array<FilterRow>();
  private _conditionTerms: Array<LMBuilderConditionTerm>;

  get conditionTerms(): Array<LMBuilderConditionTerm> {
    return this._conditionTerms;
  }

  get filterRows(): Array<FilterRow> {
    return this._filterRows;
  }

  get matchOption(): string {
    if (this.model.matchOption === LMFilterBuilderComponent.MATCH_OPTION_ALL) {
      return AppConstants.CONDITION_AND;
    } else if (this.model.matchOption === LMFilterBuilderComponent.MATCH_OPTION_ANY) {
      return AppConstants.CONDITION_OR;
    }
  }

  constructor(
    private lmUniqueIdService: LMUniqueIdService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmConfigurationService: LMConfigurationService,
    private lmFilterBuilderService: LMFilterBuilderService,
    private lmModalWrapperService: LMModalWrapperService,
  ) {
  }

  ngOnInit() {
    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;

    this.buildAnotherLabel = Soho.Locale.translate('BuildAnother');
    this.model.matchOption = LMFilterBuilderComponent.MATCH_OPTION_ALL;
    this.separator = AppConstants.CONDITION_AND;

    if (this.options.subjectInitialValue) {
      const subject: Qualifier = {
        isValid: this.options.subjectInitialValue.subjectUiFieldSpec ? true : false,
        text: this.options.subjectInitialValue.subject,
        type: this.options.subjectInitialValue.subjectUiFieldSpec?.fieldType,
        fieldType: this.options.subjectInitialValue.subjectUiFieldSpec?.lmrkFieldType
      };

      if (this.options.subjectInitialValue.subjectUiFieldSpec) {
        this.lmFilterBuilderService.updateQualifierFromUiFieldSpec(subject, this.options.subjectInitialValue.subjectUiFieldSpec);
      }

      let condition:FilterCondition  = 'equalTo';

      if (this.options.subjectInitialValue.operator) {
        condition = this.options.subjectInitialValue.operator as FilterCondition;
      }

      const predicate: Qualifier = {
        isValid: true,
        text: this.options.subjectInitialValue.predicate,
        type: this.options.subjectInitialValue.subjectUiFieldSpec?.fieldType,          // this isn't correct
        fieldType: this.options.subjectInitialValue.subjectUiFieldSpec?.lmrkFieldType  // this isn't correct
      };

      const filterRow:FilterRow = {
        uniqueIdentifier: this.buildUniqueIdentifier(),
            subject: subject,
          condition: condition,
          predicate: predicate,
          option: this.options.subjectInitialValue.predicateType as FilterOption,
      };

      this.lmFilterBuilderService.localizePredicateValue(filterRow);
      this.filterRows.push(filterRow);
    } else {
      this.createOneFilterRowEntry();
    }
  }

  private createOneFilterRowEntry() {
    this.filterRows.push({
      uniqueIdentifier: this.buildUniqueIdentifier(),
      subject:   { text: '', type: 'TEXT' },
      condition: 'equalTo',
      predicate: { text: '', type: 'TEXT' }
    });
  }

  onMatchChange(event: Event) {
    if (this.model.matchOption === LMFilterBuilderComponent.MATCH_OPTION_ALL
        && this.separator === AppConstants.CONDITION_OR) {
      this.separator = AppConstants.CONDITION_AND;
    } else if (this.model.matchOption === LMFilterBuilderComponent.MATCH_OPTION_ANY
        && this.separator === AppConstants.CONDITION_AND) {
      this.separator = AppConstants.CONDITION_OR;
    }
  }

  onRemoveButtonEvent(event: LMFilterBuilderRemoveRowEvent, index: number) {

    if (this.filterRows.length === 1) {
      this.filterRows[ 0 ].subject.text = '';
      this.filterRows[ 0 ].subject.type = 'TEXT';
      this.filterRows[ 0 ].predicate.text = '';
      this.filterRows[ 0 ].predicate.type = 'TEXT';
    } else {
      this.filterRows.splice(index, 1);
    }
  }

  onAddButtonEvent(event: Event) {
    this.createOneFilterRowEntry();
  }

  validateFilterConditions(): Observable<boolean> {

    const instanceSelection = this.lmFilterBuilderService.convertFilterRowCriteria(this.filterRows, this.matchOption);
    if (instanceSelection.trim().length === 0) {
      return of(true);
    }

    return new Observable<boolean>(observable => {
      this.lmConfigurationService.conditionIsValid(
        instanceSelection,
        this.options.lplValidationUrl,
        this.options.businessClass,
        this.options.validationType
      ).subscribe((response: LMLplValidationResponse) => {
        if ('LPL_INVALID' === response.lplStatus) {
            const buttons = [
              // tslint:disable-next-line - shadowed variable e,
              {
                id: this.lmUniqueIdService.uniqueId([ 'ValidationError', LMDialogButtonKey.OK ]),
                text: Soho.Locale.translate(LMDialogButtonKey.OK),
                isDefault: true,
                click: (e, modal) => {
                  modal.close(true);
                }
              }
            ];
            this.lmModalWrapperService
              .message()
              .message(`<span>${response?.validationError?.errorDescription || response.message}</span>`)
              .title(Soho.Locale.translate('ValidationError'))
              .buttons(buttons)
              .open();

            observable.next(false);
        } else {
          this._conditionTerms = response.conditionTerms;
          observable.next(true);
        }
        observable.complete();
      });
    });
  }

  private buildUniqueIdentifier(): number {
    return this.uniqueRowIdentifier++;
  }

  private validateFilterCriteria(filterRow: FilterRow): boolean {
    if (filterRow.subject.text === ''
      || filterRow.condition === undefined
      || filterRow.subject.text.trim().length === 0
      || filterRow.condition.trim().length === 0) {
      return false;
    }

    if (!(filterRow.subject.type === 'BOOLEAN'
      || filterRow.subject.type === 'OTO_RELATION'
      || filterRow.subject.type === 'OTM_RELATION'
      || filterRow.subject.type === 'CONDITION'
      || filterRow.subject.type === 'STATE'
      || filterRow.condition === 'entered'
      || filterRow.condition === 'notEntered')
      && (filterRow.predicate.text === '' || filterRow.predicate.text.trim().length === 0)) {
      return false;
    }

    if (filterRow.predicate.type === 'OTO_RELATION' || filterRow.predicate.type === 'OTM_RELATION') {
      return false;
    }

    return true;
  }

  public reset() {
    this._filterRows = [];
    this._conditionTerms = undefined;
    this.createOneFilterRowEntry();
  }
}
